import * as React from "react";
import { Box, MenuItem, TextField } from "@mui/material";
// (Optional) You can import SelectChangeEvent if you want explicit typing:
// import type { SelectChangeEvent } from "@mui/material/Select";

export type CountryOption = {
  code: string;      // ISO-2 like "IN"
  dialCode: string;  // e.g. "91"
  label: string;     // e.g. "India"
};

export interface PhoneFieldProps {
  countryCode?: string;                         // e.g. "91"
  onCountryCodeChange?: (code: string) => void;
  phone?: string;                               // digits only
  onPhoneChange?: (digitsOnly: string) => void;
  analytics?: (eventName: string) => void;
  placeholder?: string;                         // default: "Phone Number"
  countries?: CountryOption[];
}

const DEFAULT_COUNTRIES: CountryOption[] = [
  { code: "IN", dialCode: "91",  label: "India" },
  { code: "US", dialCode: "1",   label: "United States" },
  { code: "GB", dialCode: "44",  label: "United Kingdom" },
  { code: "AE", dialCode: "971", label: "United Arab Emirates" },
  { code: "SG", dialCode: "65",  label: "Singapore" },
  { code: "AU", dialCode: "61",  label: "Australia" },
  { code: "CA", dialCode: "1",   label: "Canada" },
  { code: "DE", dialCode: "49",  label: "Germany" },
  { code: "FR", dialCode: "33",  label: "France" },
  { code: "IT", dialCode: "39",  label: "Italy" },
  { code: "ES", dialCode: "34",  label: "Spain" },
  { code: "NL", dialCode: "31",  label: "Netherlands" },
  { code: "BE", dialCode: "32",  label: "Belgium" },
  { code: "IE", dialCode: "353", label: "Ireland" },
  { code: "NZ", dialCode: "64",  label: "New Zealand" },
  { code: "JP", dialCode: "81",  label: "Japan" },
  { code: "KR", dialCode: "82",  label: "South Korea" },
  { code: "HK", dialCode: "852", label: "Hong Kong" },
  { code: "MY", dialCode: "60",  label: "Malaysia" },
  { code: "ID", dialCode: "62",  label: "Indonesia" },
  { code: "PH", dialCode: "63",  label: "Philippines" },
  { code: "VN", dialCode: "84",  label: "Vietnam" },
  { code: "TH", dialCode: "66",  label: "Thailand" },
  { code: "BD", dialCode: "880", label: "Bangladesh" },
  { code: "PK", dialCode: "92",  label: "Pakistan" },
  { code: "LK", dialCode: "94",  label: "Sri Lanka" },
  { code: "NP", dialCode: "977", label: "Nepal" },
  { code: "CN", dialCode: "86",  label: "China" },
  { code: "TW", dialCode: "886", label: "Taiwan" },
  { code: "SA", dialCode: "966", label: "Saudi Arabia" },
  { code: "QA", dialCode: "974", label: "Qatar" },
  { code: "KW", dialCode: "965", label: "Kuwait" },
  { code: "BH", dialCode: "973", label: "Bahrain" },
  { code: "OM", dialCode: "968", label: "Oman" },
  { code: "TR", dialCode: "90",  label: "Türkiye" },
  { code: "IL", dialCode: "972", label: "Israel" },
  { code: "EG", dialCode: "20",  label: "Egypt" },
  { code: "ZA", dialCode: "27",  label: "South Africa" },
  { code: "NG", dialCode: "234", label: "Nigeria" },
  { code: "KE", dialCode: "254", label: "Kenya" },
  { code: "TZ", dialCode: "255", label: "Tanzania" },
  { code: "UG", dialCode: "256", label: "Uganda" },
  { code: "BR", dialCode: "55",  label: "Brazil" },
  { code: "MX", dialCode: "52",  label: "Mexico" },
  { code: "AR", dialCode: "54",  label: "Argentina" },
  { code: "CL", dialCode: "56",  label: "Chile" },
  { code: "CO", dialCode: "57",  label: "Colombia" },
  { code: "PE", dialCode: "51",  label: "Peru" },
  { code: "RU", dialCode: "7",   label: "Russia" },
  { code: "UA", dialCode: "380", label: "Ukraine" },
  { code: "PL", dialCode: "48",  label: "Poland" },
  { code: "CZ", dialCode: "420", label: "Czechia" },
  { code: "AT", dialCode: "43",  label: "Austria" },
  { code: "CH", dialCode: "41",  label: "Switzerland" },
  { code: "SE", dialCode: "46",  label: "Sweden" },
  { code: "NO", dialCode: "47",  label: "Norway" },
  { code: "DK", dialCode: "45",  label: "Denmark" },
  { code: "FI", dialCode: "358", label: "Finland" },
  { code: "PT", dialCode: "351", label: "Portugal" },
  { code: "GR", dialCode: "30",  label: "Greece" },
  { code: "RO", dialCode: "40",  label: "Romania" },
  { code: "HU", dialCode: "36",  label: "Hungary" },
  { code: "SK", dialCode: "421", label: "Slovakia" },
  { code: "SI", dialCode: "386", label: "Slovenia" },
  { code: "HR", dialCode: "385", label: "Croatia" },
  { code: "BG", dialCode: "359", label: "Bulgaria" },
];

const PhoneField: React.FC<PhoneFieldProps> = ({
  countryCode = "91",
  onCountryCodeChange,
  phone = "",
  onPhoneChange,
  analytics,
  placeholder = "Phone Number",
  countries = DEFAULT_COUNTRIES,
}) => {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 2 }}>
      {/* Country code select */}
      <TextField
        id="country-code"
        select
        value={countryCode}
        variant="outlined"
        size="small"
        SelectProps={{
          onChange: (event) => {
            // MUI Select types event.target.value as unknown; coerce safely
            const next = String((event as any).target.value);
            onCountryCodeChange?.(next);
            analytics?.("dialcode_field_Sign_up-page_clicked");
          },
          renderValue: (v: unknown) => `+${String(v)}`,
          MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
        }}
        inputProps={{ "aria-label": "Country code" }}
        fullWidth
      >
        {countries.map((c) => (
          <MenuItem key={`${c.code}-${c.dialCode}`} value={c.dialCode}>
            +{c.dialCode} — {c.label}
          </MenuItem>
        ))}
      </TextField>

      {/* Phone number input */}
      <TextField
        id="phone-number"
        placeholder={placeholder}
        value={phone}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const digits = (e.target.value || "").replace(/\D/g, "");
          onPhoneChange?.(digits);
          analytics?.("Phone_field_Sign_up-page_clicked");
        }}
        variant="outlined"
        size="small"
        fullWidth
        inputMode="numeric"
        autoComplete="tel"
      />
    </Box>
  );
};

export default PhoneField;
